import { useRef, useState } from "react";
import { Bug, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const SEVERITIES = [
  { key: "low", label: "Niedrig", cls: "border-neutral-300 data-[on=true]:bg-neutral-800 data-[on=true]:text-white" },
  { key: "normal", label: "Normal", cls: "border-amber-300 data-[on=true]:bg-amber-500 data-[on=true]:text-white" },
  { key: "high", label: "Hoch / Blockiert", cls: "border-red-300 data-[on=true]:bg-red-600 data-[on=true]:text-white" },
];

export function BugReportDialog({ open, onOpenChange }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("normal");
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("account_key", localStorage.getItem("rl_account") || "");
      fd.append("folder_title", "Fehlermeldungen");
      const { data } = await api.post("/upload", fd);
      setAttachment({ file_id: data.id, url: data.url, kind: data.kind, filename: file.name });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!title.trim()) {
      toast.error("Bitte gib einen kurzen Titel an");
      return;
    }
    setSending(true);
    try {
      await api.post("/bugs", {
        title: title.trim(),
        description: description.trim(),
        severity,
        page: window.location.pathname,
        attachment,
      });
      toast.success("Bug gemeldet – der Admin wurde per E-Mail benachrichtigt. Danke! 🙏");
      setTitle("");
      setDescription("");
      setSeverity("normal");
      setAttachment(null);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="bug-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-red-500" /> Bug melden
          </DialogTitle>
          <DialogDescription>
            Beschreibe kurz das Problem – die Meldung geht direkt per E-Mail an die Redaktionsleitung.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1.5 block">Was funktioniert nicht? *</label>
            <Input
              data-testid="bug-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Upload im Brand Hub bricht ab"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1.5 block">Details (was hast du gemacht, was ist passiert?)</label>
            <Textarea
              data-testid="bug-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Schritte zum Nachstellen, Fehlermeldung, Gerät…"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1.5 block">Priorität</label>
            <div className="flex gap-2">
              {SEVERITIES.map((s) => (
                <button
                  key={s.key}
                  data-testid={`bug-severity-${s.key}`}
                  data-on={severity === s.key}
                  onClick={() => setSeverity(s.key)}
                  className={`flex-1 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${s.cls}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} accept="image/*,video/*,.pdf" />
            {attachment ? (
              <div className="flex items-center gap-2 rounded-lg bg-neutral-100 px-3 py-2 text-xs text-neutral-700">
                <Paperclip className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate flex-1">{attachment.filename}</span>
                <button data-testid="bug-remove-attachment" onClick={() => setAttachment(null)}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                data-testid="bug-attach-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-300 px-3 py-2.5 text-xs font-medium text-neutral-500 hover:bg-neutral-50"
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
                Screenshot / Datei anhängen (optional)
              </button>
            )}
          </div>
          <Button data-testid="bug-submit-btn" onClick={submit} disabled={sending || uploading} className="w-full">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bug className="h-4 w-4 mr-2" />}
            Bug-Meldung senden
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
